import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    CircularProgress,
    Typography,
    useTheme
} from '@mui/material';
import { usePostHog } from '@posthog/react';
import { TrackedTextField } from 'src/theme/StyledFields';
import React, { useState } from 'react';
import { capture } from '../../analytics/posthog';
import ApiClient from '../../config/ApiClient';
import { useAppContext } from '../../context/AppContext';
import { useLessonPlanEditor } from '../../context/LessonPlanEditorContext';
import { LessonPlanSessionModel } from '../../object-actions/types/types';
import { areAllSlidesTrackedForSession } from './lessonPlanProgressUtils';
import LessonPlanSlideDeck from './LessonPlanSlideDeck';

type LessonPlanRole = 'payer' | 'athlete' | 'coach' | 'parent';

interface LessonPlanCardProps {
    session: LessonPlanSessionModel;
    expanded: boolean;
    onChange: (event: React.SyntheticEvent<Element, Event>, expanded: boolean) => void;
    cardRef?: (element: HTMLDivElement | null) => void;
    lessonPlanId?: number | null;
    myRoles?: LessonPlanRole[];
    isPublished?: boolean;
}

const LessonPlanCard: React.FC<LessonPlanCardProps> = ({
    session,
    expanded,
    onChange,
    cardRef,
    lessonPlanId,
    myRoles,
    isPublished = false
}) => {
    const theme = useTheme();
    const posthog = usePostHog();
    const { organization } = useAppContext();
    const { isEditMode, updateSession, updateSlide, getSessionWithEdits } = useLessonPlanEditor();
    const [isMarkingComplete, setIsMarkingComplete] = useState(false);

    // Get merged session data (original + edits) for previewing
    const sessionData = getSessionWithEdits(session);
    const headerText = sessionData.topic || `Session ${sessionData.session_number ?? session.id ?? ''}`;
    const subtitle = sessionData.objective ? sessionData.objective : null;
    const isCompleted = !!sessionData.completed_at;

    const allSlidesTracked = areAllSlidesTrackedForSession(sessionData); // use session data because

    const handleMarkComplete = async () => {
        if (!session?.id || isMarkingComplete || !isPublished) {
            return;
        }
        capture(posthog, "lesson_session_mark_complete_clicked", {
            lesson_plan_id: lessonPlanId != null && typeof lessonPlanId === 'number' ? lessonPlanId : undefined,
            session_id: session.id,
            session_number: sessionData.session_number,
            organization_id: organization?.id
        });
        setIsMarkingComplete(true);
        try {
            await ApiClient.post(`/api/lesson-plan-sessions/${session.id}/mark_complete/`, {});
            updateSession(session.id, { completed_at: new Date().toISOString() });
            if (lessonPlanId != null && typeof lessonPlanId === 'number') {
                capture(posthog, "lesson_session_completed", {
                    lesson_plan_id: lessonPlanId,
                    session_id: session.id,
                    session_number: sessionData.session_number,
                    organization_id: organization?.id
                });
            }
        } catch (error) {
            console.error('Error marking session as complete:', error);
        } finally {
            setIsMarkingComplete(false);
        }
    };

    return (
        <>
            <Accordion
                ref={cardRef}
                expanded={expanded}
                onChange={onChange}
                data-attr="lesson-plan-card_accordion"
                data-custom-id={String(session.id)}
                data-testid={`lesson-plan-card--session-${session.session_number ?? session.id}`}
                sx={{
                    mb: 2,
                    boxShadow: 2,
                    borderRadius: "6px 6px 0 0"
                }}
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                        backgroundColor: theme.palette.mode === 'dark'
                            ? 'rgba(25, 118, 210, 0.1)'
                            : 'rgba(25, 118, 210, 0.05)',
                        '&:hover': {
                            backgroundColor: theme.palette.mode === 'dark'
                                ? 'rgba(25, 118, 210, 0.15)'
                                : 'rgba(25, 118, 210, 0.08)',
                        },
                        '&.Mui-expanded': {
                            borderBottom: `1px solid ${theme.palette.divider}`,
                        },
                        minHeight: 64
                    }}
                >
                    <Box
                        sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                    >
                        <Box sx={{ flex: 1 }}>
                            {isEditMode ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    <TrackedTextField
                                        fullWidth
                                        size="small"
                                        label="Topic"
                                        value={sessionData.topic || ''}
                                        onChange={(e) => updateSession(session.id, { topic: e.target.value })}
                                        onClick={(e) => e.stopPropagation()}
                                        onFocus={(e) => e.stopPropagation()}
                                        variant="outlined"
                                        data-attr="lesson-plan-card_field-topic"
                                        data-testid="lesson-plan-card_field-topic"
                                    />
                                    <TrackedTextField
                                        fullWidth
                                        size="small"
                                        label="Objective"
                                        value={sessionData.objective || ''}
                                        onChange={(e) => updateSession(session.id, { objective: e.target.value })}
                                        onClick={(e) => e.stopPropagation()}
                                        onFocus={(e) => e.stopPropagation()}
                                        variant="outlined"
                                        multiline
                                        rows={2}
                                        data-attr="lesson-plan-card_field-objective"
                                        data-testid="lesson-plan-card_field-objective"
                                    />
                                    {sessionData.theme && (
                                        <TrackedTextField
                                            fullWidth
                                            size="small"
                                            label="Theme"
                                            value={sessionData.theme || ''}
                                            onChange={(e) => updateSession(session.id, { theme: e.target.value })}
                                            onClick={(e) => e.stopPropagation()}
                                            onFocus={(e) => e.stopPropagation()}
                                            variant="outlined"
                                            data-attr="lesson-plan-card_field-theme"
                                            data-testid="lesson-plan-card_field-theme"
                                        />
                                    )}
                                    {sessionData.key_tools && (
                                        <TrackedTextField
                                            fullWidth
                                            size="small"
                                            label="Key Tools"
                                            value={sessionData.key_tools || ''}
                                            onChange={(e) => updateSession(session.id, { key_tools: e.target.value })}
                                            onClick={(e) => e.stopPropagation()}
                                            onFocus={(e) => e.stopPropagation()}
                                            variant="outlined"
                                            multiline
                                            rows={2}
                                            data-attr="lesson-plan-card_field-key-tools"
                                            data-testid="lesson-plan-card_field-key-tools"
                                        />
                                    )}
                                </Box>
                            ) : (
                                <>
                                    <Typography
                                        data-testid="lesson-plan-card_header-text"
                                        variant="h6"
                                        sx={{
                                            fontWeight: 'bold',
                                            color: 'text.primary',
                                        }}
                                    >
                                        {headerText}
                                    </Typography>
                                    {subtitle && (
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            {subtitle}
                                        </Typography>
                                    )}
                                </>
                            )}
                        </Box>
                        {(myRoles?.includes('coach') || myRoles?.includes('parent')) && isPublished && allSlidesTracked ? (
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mr: 1 }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    size="small"
                                    onClick={handleMarkComplete}
                                    disabled={isCompleted || isMarkingComplete || !allSlidesTracked}
                                    startIcon={isMarkingComplete ? <CircularProgress size={16} /> : allSlidesTracked ? <TaskAltIcon fontSize="small" /> : null}
                                    title={!allSlidesTracked ? 'Complete all slides before marking as complete' : undefined}
                                    data-attr="lesson-plan-card_mark-complete"
                                    data-custom-id={String(session.id)}
                                >
                                    {isMarkingComplete ? 'Marking...' : isCompleted ? <span data-testid="lesson-plan-card-completed">Completed</span> : allSlidesTracked ? 'Mark Complete' : ''}
                                </Button>
                            </Box>
                        ) : isCompleted ? (
                            <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                disabled={isCompleted}
                                startIcon={<TaskAltIcon fontSize="small" />}
                                data-attr="lesson-plan-card_mark-complete"
                                data-custom-id={String(session.id)}
                            >
                                <span data-testid="lesson-plan-card-completed">Completed</span>
                            </Button>
                        ) : null}
                    </Box>
                </AccordionSummary>
                <AccordionDetails
                    sx={{
                        px: 1,
                        py: 0,
                        mb: 2,
                        backgroundColor: theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.02)'
                            : 'rgba(0, 0, 0, 0.01)',
                    }}
                >
                    <LessonPlanSlideDeck
                        session={sessionData}
                        lessonPlanId={lessonPlanId}
                        isExpanded={expanded}
                        myRoles={myRoles}
                        isPublished={isPublished}
                        updateSlide={updateSlide}
                        updateSession={updateSession}
                    />
                </AccordionDetails>
            </Accordion>
        </>
    );
};

export default LessonPlanCard;

